import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Table } from 'react-bootstrap';
import { calculateMarginPosition, formatPrice } from '../utils/riskCalculations';
import { MarginPositionStorage } from '../utils/riskStorage';

const MarginPercentageCalculator = () => {
  // Form state
  const [accountSize, setAccountSize] = useState(10000);
  const [riskPercentage, setRiskPercentage] = useState(1);
  const [availableMargin, setAvailableMargin] = useState(10000);
  const [marginRequirement, setMarginRequirement] = useState(5); // In percentage (5% = 20x leverage)
  const [entryPrice, setEntryPrice] = useState(100);
  const [stopLossPrice, setStopLossPrice] = useState(95);
  const [direction, setDirection] = useState('long');
  
  // Derived leverage
  const [derivedLeverage, setDerivedLeverage] = useState(20);
  
  // Margin mode options
  const [marginMode, setMarginMode] = useState('isolated');
  
  // Results state
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Calculate leverage whenever margin requirement changes
  useEffect(() => {
    if (marginRequirement > 0 && marginRequirement <= 100) {
      const leverage = 100 / marginRequirement;
      setDerivedLeverage(leverage);
    } else {
      setDerivedLeverage(1); // Default to 1x if invalid
    }
  }, [marginRequirement]);
  
  // Load from storage on first render
  useEffect(() => {
    const settings = MarginPositionStorage.loadSettings();
    setAccountSize(settings.accountSize);
    setRiskPercentage(settings.riskPercentage);
    setAvailableMargin(settings.availableMargin);
    // Convert leverage to margin requirement
    if (settings.leverage) {
      setMarginRequirement(100 / settings.leverage);
    }
    setEntryPrice(settings.entryPrice);
    setStopLossPrice(settings.stopLossPrice);
    setDirection(settings.direction);
    setMarginMode(settings.marginMode);
  }, []);
  
  // Save settings when they change
  useEffect(() => {
    const settings = {
      accountSize,
      riskPercentage,
      availableMargin,
      leverage: derivedLeverage, // Store the calculated leverage
      entryPrice,
      stopLossPrice,
      direction,
      marginMode
    };
    MarginPositionStorage.saveSettings(settings);
  }, [accountSize, riskPercentage, availableMargin, derivedLeverage, entryPrice, stopLossPrice, direction, marginMode]);
  

// Update direction only when values have fully changed
useEffect(() => {
    // Only change direction automatically if prices are valid numbers
    if (entryPrice > 0 && stopLossPrice > 0) {
      // Only update if the difference is significant enough to avoid toggle loops
      const priceDifference = entryPrice - stopLossPrice;
      
      // Use a small threshold to prevent rapid toggling when values are close
      // or when entering decimal values
      if (Math.abs(priceDifference) > 0.0001) {
        const newDirection = priceDifference > 0 ? 'long' : 'short';
        if (direction !== newDirection) {
          setDirection(newDirection);
        }
      }
    }
  }, [entryPrice, stopLossPrice, direction]);
  
  // Calculate position size
  const calculatePosition = () => {
    try {
      setError(null);
      
      // Validate margin requirement
      if (!marginRequirement || marginRequirement <= 0 || marginRequirement > 100) {
        throw new Error("Margin requirement must be between 0 and 100%");
      }
      
      // Call the utility function to calculate margin position using derived leverage
      const result = calculateMarginPosition(
        accountSize,
        riskPercentage,
        availableMargin,
        derivedLeverage,
        entryPrice,
        stopLossPrice,
        marginMode
      );
      
      // Set the results
      setResult(result);
      
    } catch (err) {
      setError(err.message);
      setResult(null);
    }
  };
  
  // Format leveraged percentage for display
  const formatLeverage = (value) => {
    if (value >= 100) {
      return `${value.toFixed(0)}x`;
    } else if (value >= 10) {
      return `${value.toFixed(1)}x`;
    } else {
      return `${value.toFixed(2)}x`;
    }
  };
  
  // Reset form to defaults
  const handleReset = () => {
    setAccountSize(10000);
    setRiskPercentage(1);
    setAvailableMargin(10000);
    setMarginRequirement(5); // 5% (20x leverage)
    setEntryPrice(100);
    setStopLossPrice(95);
    setDirection('long');
    setMarginMode('isolated');
    setResult(null);
    setError(null);
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="card-header-gradient">
        <h4 className="mb-0">
          <i className="bi bi-calculator-fill me-2"></i>
          Margin Percentage Calculator
        </h4>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Form>
          <Row className="mb-4">
            <Col lg={6}>
              <Card className="bg-dark mb-3">
                <Card.Header className="bg-dark border-bottom border-secondary">
                  <h5 className="mb-0">Account Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Account Size</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <Form.Control
                            type="number"
                            min="0"
                            step="100"
                            value={accountSize}
                            onChange={(e) => setAccountSize(parseFloat(e.target.value))}
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Total trading capital
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Risk Percentage</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            min="0.1"
                            step="0.1"
                            max="100"
                            value={riskPercentage}
                            onChange={(e) => setRiskPercentage(parseFloat(e.target.value))}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        <Form.Text className="text-muted">
                          Per trade risk (typically 1-2%)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={6}>
              <Card className="bg-dark mb-3">
                <Card.Header className="bg-dark border-bottom border-secondary">
                  <h5 className="mb-0">Margin Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Available Margin</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <Form.Control
                            type="number"
                            min="0"
                            step="100"
                            value={availableMargin}
                            onChange={(e) => setAvailableMargin(parseFloat(e.target.value))}
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Margin available for this trade
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Margin Requirement</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            min="0.1"
                            max="100"
                            step="0.1"
                            value={marginRequirement}
                            onChange={(e) => setMarginRequirement(parseFloat(e.target.value))}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        <Form.Text className="text-muted">
                          {marginRequirement}% margin = {formatLeverage(derivedLeverage)} leverage
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-1">
                    <Form.Label>Margin Mode</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="margin-isolated"
                        label="Isolated"
                        checked={marginMode === 'isolated'}
                        onChange={() => setMarginMode('isolated')}
                        className="me-3"
                      />
                      <Form.Check
                        type="radio"
                        id="margin-cross"
                        label="Cross"
                        checked={marginMode === 'cross'}
                        onChange={() => setMarginMode('cross')}
                      />
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          <Card className="bg-dark mb-4">
            <Card.Header className="bg-dark border-bottom border-secondary">
              <h5 className="mb-0">Trade Settings</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Trade Direction</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="direction-long"
                        label="Long"
                        checked={direction === 'long'}
                        onChange={() => {
                          setDirection('long');
                          if (stopLossPrice >= entryPrice) {
                            // Set to 5% below entry for long positions
                            setStopLossPrice(Math.max(0.01, entryPrice * 0.95));
                          }
                        }}
                        className="me-3"
                      />
                      <Form.Check
                        type="radio"
                        id="direction-short"
                        label="Short"
                        checked={direction === 'short'}
                        onChange={() => {
                          setDirection('short');
                          if (stopLossPrice <= entryPrice) {
                            // Set to 5% above entry for short positions
                            setStopLossPrice(entryPrice * 1.05);
                          }
                        }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Entry Price</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <Form.Control
                        type="number"
                        min="0.0001"
                        step="0.01"
                        value={entryPrice}
                        onChange={(e) => setEntryPrice(parseFloat(e.target.value))}
                      />
                    </div>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Stop Loss Price
                      <span className={`ms-2 badge ${direction === 'long' ? 'bg-danger' : 'bg-success'}`}>
                        {direction === 'long' ? 'Below Entry' : 'Above Entry'}
                      </span>
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <Form.Control
                        type="number"
                        min="0.0001"
                        step="0.00001"
                        value={stopLossPrice}
                        onChange={(e) => setStopLossPrice(parseFloat(e.target.value))}
                        className={stopLossPrice === entryPrice ? 'is-invalid' : ''}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          <div className="d-flex mt-4 mb-4">
            <Button variant="primary" onClick={calculatePosition} className="me-2 py-2 px-4">
              <i className="bi bi-calculator me-2"></i>
              Calculate Position Size
            </Button>
            <Button variant="outline-secondary" onClick={handleReset} className="px-3">
              <i className="bi bi-arrow-repeat me-1"></i>
              Reset
            </Button>
          </div>
        </Form>
        
        {result && (
          <div className="results-section mt-4">
            <h5 className="mb-4 position-relative">
              <span className="gradient-text">Position Size Results</span>
            </h5>
            
            <Row>
              <Col lg={6}>
                <Card className="bg-dark mb-3">
                  <Card.Header className="bg-dark border-bottom border-secondary">
                    <h5 className="mb-0">Position Details</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive className="mb-0">
                      <tbody>
                        <tr>
                          <td>Position Type</td>
                          <td>
                            <span className={`badge ${result.direction === 'long' ? 'signal-badge-long' : 'signal-badge-short'}`}>
                              {result.direction.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Position Size</td>
                          <td><strong>{result.positionSizeUnits.toLocaleString(undefined, { maximumFractionDigits: 8 })}</strong> units</td>
                        </tr>
                        <tr>
                          <td>Position Value</td>
                          <td>${formatPrice(result.positionValue)}</td>
                        </tr>
                        <tr>
                          <td>Leveraged Exposure</td>
                          <td>${formatPrice(result.leveragedExposure)}</td>
                        </tr>
                        <tr>
                          <td>Margin Required</td>
                          <td>${formatPrice(result.marginRequired)} ({marginRequirement}%)</td>
                        </tr>
                        <tr>
                          <td>Limiting Factor</td>
                          <td>
                            <span className={`badge bg-${result.limitingFactor === 'Risk' ? 'info' : 'warning'}`}>
                              {result.limitingFactor}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={6}>
                <Card className="bg-dark mb-3">
                  <Card.Header className="bg-dark border-bottom border-secondary">
                    <h5 className="mb-0">Risk Analysis</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive className="mb-0">
                      <tbody>
                        <tr>
                          <td>Risk Per Unit</td>
                          <td>${formatPrice(result.riskPerUnit)}</td>
                        </tr>
                        <tr>
                          <td>Total Risk Amount</td>
                          <td>${formatPrice(result.actualRiskAmount)}</td>
                        </tr>
                        <tr>
                          <td>Risk Percentage</td>
                          <td>{result.actualRiskPercentage.toFixed(2)}% of account</td>
                        </tr>
                        <tr>
                          <td>Stop Loss Distance</td>
                          <td>{result.stopDistance.toFixed(2)}% from entry</td>
                        </tr>
                        <tr>
                          <td>Liquidation Price</td>
                          <td>
                            ${formatPrice(result.liquidationPrice)}
                            {result.liquidationPrice > stopLossPrice && direction === 'long' && (
                              <span className="badge bg-danger ms-2">Warning: Above Stop Loss</span>
                            )}
                            {result.liquidationPrice < stopLossPrice && direction === 'short' && (
                              <span className="badge bg-danger ms-2">Warning: Below Stop Loss</span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Liquidation Distance</td>
                          <td>{result.liquidationDistance.toFixed(2)}% from entry</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            {/* Position size limits explanation */}
            <Row>
              <Col md={12}>
                <Card className="bg-dark">
                  <Card.Header className="bg-dark border-bottom border-secondary">
                    <h5 className="mb-0">Position Size Constraints</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><strong>Maximum by Risk:</strong> {result.maxPositionByRisk.toLocaleString()} units</span>
                        <span>(${formatPrice(result.maxRiskAmount)} risk)</span>
                      </div>
                      <div className="progress" style={{ height: '25px' }}>
                        <div
                          className={`progress-bar ${result.limitingFactor === 'Risk' ? 'bg-info' : 'bg-secondary'}`}
                          role="progressbar"
                          style={{ width: '100%' }}
                        >
                          Risk Limit: {riskPercentage}% of ${accountSize.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><strong>Maximum by Margin:</strong> {result.maxPositionByMargin.toLocaleString()} units</span>
                        <span>({(availableMargin * derivedLeverage / entryPrice).toLocaleString(undefined, { minimumFractionDigits: 0 })} with {formatLeverage(derivedLeverage)} leverage)</span>
                      </div>
                      <div className="progress" style={{ height: '25px' }}>
                        <div
                          className={`progress-bar ${result.limitingFactor === 'Margin' ? 'bg-warning' : 'bg-secondary'}`}
                          role="progressbar"
                          style={{ width: '100%' }}
                        >
                          Margin Limit: ${availableMargin.toLocaleString()} at {marginRequirement}% requirement
                        </div>
                      </div>
                    </div>
                    
                    {result.limitingFactor === 'Risk' ? (
                      <Alert variant="info" className="mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Position limited by risk:</strong> Your position size is determined by your risk management settings, 
                        not margin constraints. This is ideal! You still have {Math.round((1 - result.marginRequired/availableMargin) * 100)}% 
                        of available margin remaining.
                      </Alert>
                    ) : (
                      <Alert variant="warning" className="mb-0">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Position limited by margin:</strong> You don't have enough available margin to take the full position 
                        size allowed by your risk settings. Consider reducing position size, increasing available margin, 
                        or decreasing the margin requirement (increasing leverage).
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            {/* Check if liquidation price is too close to stop loss */}
            {((result.liquidationPrice > stopLossPrice && direction === 'long') || 
               (result.liquidationPrice < stopLossPrice && direction === 'short')) && (
              <Alert variant="danger" className="mt-3">
                <h6 className="alert-heading mb-2">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  Liquidation Risk Warning
                </h6>
                <p className="mb-1">
                  Your liquidation price is {direction === 'long' ? 'above' : 'below'} your stop loss. 
                  This means your position could be liquidated before your stop loss is triggered.
                </p>
                <ul className="mb-0 mt-2">
                  <li>Consider decreasing the margin requirement (increasing leverage)</li>
                  <li>Add more available margin</li>
                  <li>Adjust your entry or stop loss price</li>
                  <li>Decrease position size</li>
                </ul>
              </Alert>
            )}
            
            {/* Warning for high leverage */}
            {derivedLeverage > 20 && (
              <Alert variant="warning" className="mt-3">
                <h6 className="alert-heading mb-2">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  High Leverage Warning
                </h6>
                <p className="mb-0">
                  You're using {formatLeverage(derivedLeverage)} leverage (only {marginRequirement}% margin required), 
                  which significantly increases both potential profit and risk. 
                  High leverage can lead to rapid liquidation during volatile market conditions. Consider using a higher 
                  margin requirement for better risk management.
                </p>
              </Alert>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default MarginPercentageCalculator;